/**
 * Customers — top-level customer-centric view with two internal tabs:
 *   1. Overview — KPI dashboard + breakdowns (top customers, branches)
 *   2. List     — searchable + sortable customer table with clickable rows
 *
 * All tab + filter + pagination state lives in the URL query string so the
 * back button works: leaving the list to view a customer detail puts
 * /customers/:id on the stack, and back returns to /customers?tab=list&...
 * with every filter and page intact.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, ChevronLeft, ChevronRight, Users as UsersIcon,
  Phone as PhoneIcon, Wallet, ShoppingBag, AlertCircle,
  ArrowUp, ArrowDown, ArrowUpDown, BarChart3, List as ListIcon,
  Building2, Activity, PhoneCall, Star,
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v) => {
  if (v == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 }).format(Number(v)); }
  catch { return `SAR ${Number(v).toLocaleString()}`; }
};
const intFmt = (v) => v == null ? '—' : Number(v).toLocaleString();
const dateFmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// =====================================================================
// Overview tab — KPI cards + breakdowns
// =====================================================================
const Overview = () => {
  const { showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get('/api/customers/overview')
      .then(r => { if (active) setData(r.data); })
      .catch(e => { if (active) showError('Failed to load overview: ' + (e.response?.data?.error || e.message)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [showError]);

  if (loading) return <div className="shop-loading"><div className="shop-spinner" /></div>;
  if (!data) return <div className="shop-empty">Overview unavailable.</div>;

  const ov = data.overview || {};
  const customersWithoutOrders = (ov.total_customers || 0) - (ov.customers_with_orders || 0);
  const avgOrderValue = ov.total_orders > 0 ? Number(ov.lifetime_revenue) / Number(ov.total_orders) : 0;
  const avgLtv = ov.customers_with_orders > 0 ? Number(ov.lifetime_revenue) / Number(ov.customers_with_orders) : 0;

  return (
    <div>
      {/* Top KPI row */}
      <div className="shop-kpis">
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon blue"><UsersIcon size={14} /></span> Total customers</div>
          <div className="shop-kpi-value blue">{intFmt(ov.total_customers)}</div>
          <div className="shop-kpi-sub">{intFmt(ov.customers_with_orders)} have orders · {intFmt(customersWithoutOrders)} have none yet</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon green"><Wallet size={14} /></span> Lifetime revenue</div>
          <div className="shop-kpi-value green">{moneyFmt(ov.lifetime_revenue)}</div>
          <div className="shop-kpi-sub">Avg per customer: {moneyFmt(avgLtv)}</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon amber"><AlertCircle size={14} /></span> Outstanding</div>
          <div className="shop-kpi-value amber">{moneyFmt(ov.total_outstanding)}</div>
          <div className="shop-kpi-sub">Received: {moneyFmt(ov.total_received)}</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon neutral"><ShoppingBag size={14} /></span> Total orders</div>
          <div className="shop-kpi-value neutral">{intFmt(ov.total_orders)}</div>
          <div className="shop-kpi-sub">Avg order value: {moneyFmt(avgOrderValue)} · Canceled: {intFmt(ov.canceled_orders)}</div>
        </div>
      </div>

      {/* Phones / coverage row */}
      <div className="shop-kpis">
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon blue"><PhoneIcon size={14} /></span> Customers with phone</div>
          <div className="shop-kpi-value blue">{intFmt(ov.customers_with_phone)}</div>
          <div className="shop-kpi-sub">{intFmt(ov.total_phones)} total numbers tracked (incl. order-level)</div>
        </div>
        <div className="shop-kpi">
          <div className="shop-kpi-head"><span className="shop-kpi-icon neutral"><Activity size={14} /></span> Order date range</div>
          <div className="shop-kpi-value neutral" style={{ fontSize: '1.1rem' }}>{dateFmt(ov.earliest_order)} → {dateFmt(ov.most_recent_order)}</div>
          <div className="shop-kpi-sub">Earliest to most recent</div>
        </div>
      </div>

      {/* Top customers */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Top 10 customers by lifetime value</h3>
        </div>
        {data.topCustomers?.length === 0 ? (
          <div className="shop-empty">No order data yet.</div>
        ) : (
          <table className="shop-table">
            <thead><tr><th>#</th><th>Customer</th><th>City</th><th>Orders</th><th>Lifetime value</th><th>Outstanding</th><th>Last order</th></tr></thead>
            <tbody>
              {(data.topCustomers || []).map((c, i) => (
                <ClickableRow key={c.customer_account} to={`/customers/${encodeURIComponent(c.customer_account)}`}>
                  <td>{i + 1}</td>
                  <td>
                    <div>{c.name_alias || c.customer_account}</div>
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>{c.customer_account}</div>
                  </td>
                  <td>{c.address_city || '—'}</td>
                  <td>{intFmt(c.total_orders)}</td>
                  <td style={{ fontWeight: 600, color: '#10b981' }}>{moneyFmt(c.lifetime_value)}</td>
                  <td style={{ color: c.outstanding_balance > 0 ? '#fbbf24' : 'rgba(255,255,255,0.4)' }}>
                    {c.outstanding_balance > 0 ? moneyFmt(c.outstanding_balance) : '—'}
                  </td>
                  <td className="shop-cell-time">{dateFmt(c.last_order_date)}</td>
                </ClickableRow>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Branch + Status side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '1rem' }}>
        <div className="shop-panel">
          <div className="shop-panel-head"><h3><Building2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Orders by branch</h3></div>
          {data.branchBreakdown?.length === 0 ? <div className="shop-empty">No data.</div> : (
            <table className="shop-table">
              <thead><tr><th>Branch</th><th>Orders</th><th>Revenue</th><th>Outstanding</th></tr></thead>
              <tbody>
                {(data.branchBreakdown || []).map(b => (
                  <tr key={b.branch}>
                    <td>{b.branch}</td>
                    <td>{intFmt(b.order_count)}</td>
                    <td style={{ color: '#10b981' }}>{moneyFmt(b.revenue)}</td>
                    <td style={{ color: b.outstanding > 0 ? '#fbbf24' : 'rgba(255,255,255,0.4)' }}>{b.outstanding > 0 ? moneyFmt(b.outstanding) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="shop-panel">
          <div className="shop-panel-head"><h3>Order processing status</h3></div>
          {data.statusBreakdown?.length === 0 ? <div className="shop-empty">No data.</div> : (
            <table className="shop-table">
              <thead><tr><th>Status</th><th>Orders</th><th>Revenue</th></tr></thead>
              <tbody>
                {(data.statusBreakdown || []).map(s => (
                  <tr key={s.status}>
                    <td><span className={`shop-badge ${s.status.toLowerCase()}`}>{s.status}</span></td>
                    <td>{intFmt(s.order_count)}</td>
                    <td>{moneyFmt(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Clickable row helper — entire <tr> navigates to `to`
// =====================================================================
const ClickableRow = ({ to, children }) => {
  const navigate = useNavigate();
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      onClick={() => navigate(to)}
      style={{ cursor: 'pointer' }}
    >
      {children}
    </motion.tr>
  );
};

// =====================================================================
// Sortable column header
// =====================================================================
const SortHeader = ({ label, columnKey, currentKey, currentDir, onClick, align = 'left' }) => {
  const isActive = currentKey === columnKey;
  const Icon = !isActive ? ArrowUpDown : (currentDir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      onClick={() => onClick(columnKey)}
      style={{ cursor: 'pointer', userSelect: 'none', textAlign: align }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.35 }} />
      </span>
    </th>
  );
};

// =====================================================================
// Customers list tab
//
// All filter/sort/pagination state is read from the URL query string
// (?tab=list&search=...&sortBy=...&hasCalls=...&page=...) so the back
// button restores the exact list view the user left when they clicked
// into a customer detail page.
// =====================================================================
const CustomersList = () => {
  const { showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL — single source of truth.
  const search   = searchParams.get('search') || '';
  const sortBy   = searchParams.get('sortBy') || 'name';
  const sortDir  = searchParams.get('sortDir') || (sortBy === 'name' ? 'asc' : 'desc');
  const hasCalls = searchParams.get('hasCalls') === 'true';
  const hasNps   = searchParams.get('hasNps') === 'true';
  const page     = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const pageSize = Math.min(100, Math.max(10, parseInt(searchParams.get('pageSize'), 10) || 25));

  // Local mirror of search so typing isn't debounced through URL changes
  // every keystroke. Initial value seeded from URL on mount.
  const [searchInput, setSearchInput] = useState(search);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Patch URL params, preserving keys we don't touch (e.g. tab=list).
  // replace:true so each filter tweak doesn't pile up history entries —
  // back button still goes to whatever page was before /customers.
  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === '' || v == null || v === false) next.delete(k);
        else next.set(k, String(v));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounce: typed search → URL after 350ms idle. Reset to page 1 when
  // search changes so we don't request page 8 of a freshly filtered set.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) updateParams({ search: searchInput, page: 1 });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, updateParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sortBy, sortDir });
      if (search)         params.set('search', search);
      if (hasCalls)       params.set('hasCalls', 'true');
      if (hasNps)         params.set('hasNps', 'true');
      const res = await api.get(`/api/customers?${params}`);
      setRows(res.data.customers || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (e) {
      showError('Failed to load customers: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sortBy, sortDir, hasCalls, hasNps, showError]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (colKey) => {
    let nextDir;
    if (sortBy === colKey) nextDir = sortDir === 'asc' ? 'desc' : 'asc';
    else nextDir = colKey === 'name' ? 'asc' : 'desc';
    updateParams({ sortBy: colKey, sortDir: nextDir, page: 1 });
  };

  return (
    <div className="shop-panel">
      <div className="shop-tab-toolbar" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 360 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            className="shop-select"
            style={{ paddingLeft: 32, width: '100%' }}
            placeholder="Search by name, account, or phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={hasCalls}
            onChange={(e) => updateParams({ hasCalls: e.target.checked ? 'true' : '', page: 1 })}
            style={{ cursor: 'pointer' }}
          />
          <PhoneCall size={13} /> Has Avaya call
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={hasNps}
            onChange={(e) => updateParams({ hasNps: e.target.checked ? 'true' : '', page: 1 })}
            style={{ cursor: 'pointer' }}
          />
          <Star size={13} /> Has NPS rating
        </label>

        <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginLeft: 'auto' }}>
          Click any column header to sort. Click any row to open the customer.
        </span>
      </div>

      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="shop-empty">
          <UsersIcon size={32} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: '0.5rem' }}>
            {search ? 'No customers match your search.' : 'No customers yet.'}
          </div>
        </div>
      ) : (
        <>
          <table className="shop-table">
            <thead>
              <tr>
                <SortHeader label="Customer" columnKey="name" currentKey={sortBy} currentDir={sortDir} onClick={handleSort} />
                <th>Phone</th>
                <th>City</th>
                <SortHeader label="Orders" columnKey="orders" currentKey={sortBy} currentDir={sortDir} onClick={handleSort} />
                <SortHeader label="Lifetime value" columnKey="ltv" currentKey={sortBy} currentDir={sortDir} onClick={handleSort} />
                <SortHeader label="Outstanding" columnKey="outstanding" currentKey={sortBy} currentDir={sortDir} onClick={handleSort} />
                <SortHeader label="Last order" columnKey="recent" currentKey={sortBy} currentDir={sortDir} onClick={handleSort} />
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <ClickableRow key={c.customer_account} to={`/customers/${encodeURIComponent(c.customer_account)}`}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{c.name_alias || c.customer_account}</div>
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)' }}>{c.customer_account}</div>
                  </td>
                  <td>
                    {c.primary_phone_e164 ? (
                      <span style={{ fontSize: '0.78rem' }}><PhoneIcon size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.primary_phone_e164}</span>
                    ) : (c.primary_phone_raw ? (
                      <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>{c.primary_phone_raw}</span>
                    ) : '—')}
                  </td>
                  <td>{c.address_city || '—'}</td>
                  <td>{intFmt(c.total_orders)}</td>
                  <td style={{ fontWeight: 600, color: c.lifetime_value > 0 ? '#10b981' : 'rgba(255,255,255,0.4)' }}>{moneyFmt(c.lifetime_value)}</td>
                  <td style={{ color: c.outstanding_balance > 0 ? '#fbbf24' : 'rgba(255,255,255,0.4)' }}>
                    {c.outstanding_balance > 0 ? moneyFmt(c.outstanding_balance) : '—'}
                  </td>
                  <td className="shop-cell-time">{dateFmt(c.last_order_date)}</td>
                </ClickableRow>
              ))}
            </tbody>
          </table>

          <div className="shop-pagination">
            <div>
              Show <select className="shop-select" style={{ display: 'inline-block', padding: '0.25rem 0.5rem' }} value={pageSize} onChange={(e) => updateParams({ pageSize: Number(e.target.value), page: 1 })}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select> per page
            </div>
            <div className="shop-pagination-controls">
              <button className="shop-page-btn" disabled={page <= 1} onClick={() => updateParams({ page: page - 1 })}><ChevronLeft size={14} /> Prev</button>
              <span className="shop-page-info">Page {page} of {pagination.totalPages || 1}</span>
              <button className="shop-page-btn" disabled={page >= (pagination.totalPages || 1)} onClick={() => updateParams({ page: page + 1 })}>Next <ChevronRight size={14} /></button>
            </div>
            <div className="shop-page-total">{pagination.total.toLocaleString()} customers total</div>
          </div>
        </>
      )}
    </div>
  );
};

// =====================================================================
// Top-level Customers component — renders the active tab.
//
// Active tab is held in the URL (?tab=overview|list) so the back button
// from a customer detail page returns to the same tab + filters the
// user came from, instead of resetting to overview.
// =====================================================================
const Customers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'list' ? 'list' : 'overview';

  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') {
        // overview is the default — drop tab AND any list-only filters so
        // we don't carry stale ?search=... onto the overview view.
        next.delete('tab');
        next.delete('search');
        next.delete('sortBy');
        next.delete('sortDir');
        next.delete('hasCalls');
        next.delete('hasNps');
        next.delete('page');
        next.delete('pageSize');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  };

  return (
    <div className="shop">
      <div className="shop-header">
        <div>
          <h1 className="shop-title">Customers</h1>
          <p className="shop-subtitle">Unified Dynamics 365 view — orders, calls, NPS, all in one place</p>
        </div>
      </div>

      <div className="shop-tabs">
        <button
          className={`shop-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={14} /> Overview
        </button>
        <button
          className={`shop-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          <ListIcon size={14} /> Customers list
        </button>
      </div>

      {activeTab === 'overview' && <Overview />}
      {activeTab === 'list' && <CustomersList />}
    </div>
  );
};

export default Customers;
